import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import '@chancenwerk/client/design/components/cr-chip.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import {
  LogistikEntireTrip,
  LogistikIndividualOrderItem,
  LogistikLearningMaterialOrderItem,
  LogistikOrder,
  LogistikOrderChancenBriefItem,
  LogistikOrderClearanceState,
  LogistikOrderItem,
  LogistikOtherOrder,
} from '@chancenwerk/db/LogistikOrder.js';
import {
  ChancenBrief,
  LearningMaterial,
  LogistikProduct,
  TravelCertificate,
  WorkshopPack,
} from '@chancenwerk/db/LogistikProduct.js';
import { LogistikShippingStatus } from '@chancenwerk/db/enums/LogistikEnums.js';
import '@chancenwerk/design/components/cr-flex.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { filter, map, of, switchMap } from 'rxjs';
import { g_getProductCategoryString } from '../helper.js';

/**
 * @customElement logistik-edit-order-item
 */
@customElement('logistik-edit-order-item')
export class LogistikEditOrderItem extends ChemistryLitElement {
  @property({ attribute: false }) order?: LogistikOrder;
  @property({ attribute: false }) orderItem = new RXController<
    Maybe<LogistikOrderItem | LogistikIndividualOrderItem | LogistikEntireTrip | LogistikOtherOrder>
  >(this, undefined);
  @property({ attribute: false }) index = new RXController<number>(this, 0);

  @state() private readonly _product = new RXController(
    this,
    this.orderItem.observable.pipe(
      map(item => (item as LogistikOrderItem).productID),
      filter(Boolean),
      switchMap(id =>
        id ? LogistikProduct.getByID<LogistikProduct>(id)(LogistikProduct.allFields) : of(new LogistikProduct({})),
      ),
    ),
  );
  @state() private readonly _currentUser = new RXController(this, currentUser());

  override render() {
    if (!this.orderItem.value) return nothing;
    if (this.orderItem.value instanceof LogistikOrderItem)
      if (!this._product.value) return nothing;
      else return this._logistikProductContent();
    else if (this.orderItem.value instanceof LogistikEntireTrip) return this._entireTripContent(this.orderItem.value);
    else if (this.orderItem.value instanceof LogistikOtherOrder)
      return this._handwerklicheUnterstuetzungContent(this.orderItem.value);
    else return this._inidivualOrderDetails();
  }

  private _logistikProductContent() {
    if (!this._product.value || !this.orderItem.value) return nothing;
    return html`
      <cr-flex
        space="sm"
        ${css({
          background: this.index.value % 2 ? '#f1f1f1' : '#fafafa',
          padding: `${this.spacing.xs} ${this.spacing.md}`,
        })}
      >
        <cr-flex space="sm" horizontal crossaxis-center>
          <cr-image .value=${this._product.value.imageID} disabled mode="width" ${css({ width: '60px' })}></cr-image>
          <cr-flex horizontal flex crossaxis-center space="xl">
            <cr-flex flex space="sm">
              <cr-flex horizontal>
                <cr-text bold> ${g_getProductCategoryString(this._product.value._class)} </cr-text>
                ${this._digitalBadge()} ${this._selfprintBadge()}
              </cr-flex>
              <cr-text ${css({ fontSize: '1.1rem' })}>${this._product.value.title}</cr-text>
            </cr-flex>
            <cr-flex horizontal crossaxis-center space="sm">
              ${this._chancenBriefButtons()} ${this._quantityInput()} ${this._deleteIconButton()}
            </cr-flex>
          </cr-flex>
        </cr-flex>
        ${this._product.value instanceof WorkshopPack
          ? html`
              <cr-flex space="sm">
                <cr-text>${`Teilnehmer: ${this.orderItem.value.participantsQuantity}`}</cr-text>
                <cr-text>${`Trainer:innen: ${this.orderItem.value.trainersQuantity}`}</cr-text>
              </cr-flex>
            `
          : nothing}
        ${this._product.value instanceof LearningMaterial
          ? html`
              <cr-flex horizontal space="sm">
                ${this._product.value.solutionPDFID
                  ? html`
                      <cr-button
                        mode="outline"
                        flex
                        icon="visibility_lock"
                        href=${AdornisFile.getServeLink(this._product.value.solutionPDFID)}
                        target="_blank"
                      >
                        Lösungsheft
                        <cr-tooltip
                          .text=${html` <cr-text> Lösungsheft für dieses CHANCENheft herunterladen </cr-text> `}
                        ></cr-tooltip>
                      </cr-button>
                    `
                  : nothing}
                ${this._product.value.handoutPDFID
                  ? html`
                      <cr-button
                        mode="outline"
                        flex
                        icon="description"
                        href=${AdornisFile.getServeLink(this._product.value.handoutPDFID)}
                        target="_blank"
                      >
                        Handreichung
                        <cr-tooltip
                          .text=${html` <cr-text> Handreichung für dieses CHANCENheft herunterladen </cr-text> `}
                        ></cr-tooltip>
                      </cr-button>
                    `
                  : nothing}
              </cr-flex>
            `
          : nothing}
      </cr-flex>
    `;
  }

  private _digitalBadge(): Renderable {
    if (
      !this.orderItem.value ||
      this.orderItem.value._class !== LogistikLearningMaterialOrderItem._class ||
      !(this.orderItem.value as LogistikLearningMaterialOrderItem).isDigital
    ) {
      return nothing;
    }
    return html`
      <cr-chip ${css({ background: this.colors.primary, borderRadius: '5px', marginLeft: '6px', marginTop: '-3px' })}>
        <cr-text ${css({ fontSize: '12px', padding: '3px' })}> DIGITAL </cr-text>
      </cr-chip>
    `;
  }

  private _selfprintBadge(): Renderable {
    if (
      !this.orderItem.value ||
      this.orderItem.value._class !== LogistikOrderChancenBriefItem._class ||
      !(this.orderItem.value as LogistikOrderChancenBriefItem).isSelfPrint
    ) {
      return nothing;
    }
    return html`
      <cr-chip ${css({ background: this.colors.primary, borderRadius: '5px', marginLeft: '6px', marginTop: '-3px' })}>
        <cr-text ${css({ fontSize: '12px', padding: '3px' })}> SELBSTDRUCK </cr-text>
      </cr-chip>
    `;
  }

  private _inidivualOrderDetails() {
    if (!(this.orderItem.value instanceof LogistikIndividualOrderItem)) return nothing;
    return html`
      <cr-flex
        horizontal
        space="sm"
        crossaxis-start
        ${css({
          background: this.index.value % 2 ? '#f1f1f1' : '#fafafa',
          padding: `${this.spacing.xs} ${this.spacing.md}`,
        })}
      >
        <cr-flex space="md">
          <cr-flex space="sm">
            <cr-text bold> Individuelle Bestellung </cr-text>
            <cr-flex space="xs">
              <cr-text> ${this.orderItem.value.name} </cr-text>
              <cr-text tone="subtle"> ${this.orderItem.value.type} </cr-text>
            </cr-flex>
          </cr-flex>
          <cr-text> <cr-link href=${this.orderItem.value.link}> Zum Bestell-Link </cr-link> </cr-text>
          <cr-flex space="sm">
            <cr-text tone="subtle"> Verwendungszweck </cr-text>
            <cr-text> ${this.orderItem.value.purpose || '-'} </cr-text>
          </cr-flex>
          <cr-flex space="sm">
            <cr-text tone="subtle"> Begründung </cr-text>
            <cr-text> ${this.orderItem.value.explanation || '-'} </cr-text>
          </cr-flex>
          <cr-flex horizontal crossaxis-center space="sm">
            <cr-text> Ist mit Alternativ-Produkt einverstanden: </cr-text>
            <cr-text bold> ${this.orderItem.value.acceptAlternative ? 'Ja' : 'Nein'} </cr-text>
          </cr-flex>
          ${this.order?.clearanceState && this.order.clearanceState !== LogistikOrderClearanceState.NEEDS_CLEARANCE
            ? html`
                <cr-flex>
                  <cr-text tone="subtle">
                    ${this.order.clearanceState === LogistikOrderClearanceState.APPROVED ? 'Freigegeben' : 'Abgelehnt'}
                    mit Begründung:
                  </cr-text>
                  <cr-text> ${unsafeHTML(this.order.clearanceNote || '-')} </cr-text>
                </cr-flex>
              `
            : nothing}
        </cr-flex>
        <cr-flex space="sm">
          <cr-text bold> ${this.orderItem.value.price}&euro; </cr-text>
          ${this._quantityInput()} ${this._deleteIconButton()}
        </cr-flex>
      </cr-flex>
    `;
  }

  private _entireTripContent(orderItem: LogistikEntireTrip) {
    return html`
      <cr-flex
        horizontal
        space="sm"
        crossaxis-start
        ${css({
          background: this.index.value % 2 ? '#f1f1f1' : '#fafafa',
          padding: `${this.spacing.xs} ${this.spacing.md}`,
        })}
      >
        <cr-flex space="md">
          <cr-flex space="sm">
            <cr-text bold> Fahrt und Unterkunft </cr-text>
            <cr-flex space="sm">
              <cr-flex space="sm">
                <cr-text bold> Grund: </cr-text>
                <cr-text>
                  <x-plain-textarea .value=${orderItem.reason} disabled></x-plain-textarea>
                </cr-text>
              </cr-flex>
              <cr-flex space="xs">
                <cr-text bold> Vorschlag zu Fahrt und Unterkunft: </cr-text>
                <cr-text>
                  <x-plain-textarea .value=${orderItem.description} disabled></x-plain-textarea>
                </cr-text>
              </cr-flex>
              <cr-flex horizontal space="xs">
                <cr-text bold> Alter bei Fahrtantritt: </cr-text>
                <cr-text> ${orderItem.ageAtDeparture ?? '-'} </cr-text>
              </cr-flex>
              <cr-text> <b>BahnCard:</b> ${orderItem.bahncard} </cr-text>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _handwerklicheUnterstuetzungContent(orderItem: LogistikOtherOrder) {
    return html`
      <cr-flex
        horizontal
        space="sm"
        crossaxis-start
        ${css({
          background: this.index.value % 2 ? '#f1f1f1' : '#fafafa',
          padding: `${this.spacing.xs} ${this.spacing.md}`,
        })}
      >
        <cr-flex space="md">
          <cr-flex space="sm">
            <cr-text bold> Handwerkliche Unterstützung </cr-text>
            <cr-flex space="xs">
              <cr-text>
                <x-plain-textarea .value=${orderItem.description} disabled></x-plain-textarea>
              </cr-text>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _chancenBriefButtons() {
    if (!this._product.value || !this.orderItem.value || !(this.orderItem.value instanceof LogistikOrderItem))
      return nothing;
    const canEdit =
      !!this._currentUser.value ||
      (this.order.status === LogistikShippingStatus.NEW &&
        DateTime.now() < this.order.createdAt.plus({ hours: 24 }) &&
        !this.order.isExpress);
    const orderItem = this.orderItem.value as LogistikOrderChancenBriefItem;
    return this._product.value instanceof ChancenBrief || this._product.value instanceof TravelCertificate
      ? html`
          ${canEdit
            ? html`
                <cr-button
                  mode="icon"
                  icon="edit"
                  @click=${async () => {
                    if (
                      await ChromiumDialog.showPopup<boolean>({
                        content: resolve => html`
                          <x-flex padding="lg" space="lg">
                            <cr-h3> CHANCENbrief-Texte anpassen </cr-h3>
                            <logistik-component-edit-chancen-brief-variables
                              .product=${this._product.value}
                              .orderItem=${this.orderItem.value}
                            ></logistik-component-edit-chancen-brief-variables>
                            <x-flex horizontal end space="sm">
                              <cr-button
                                mode="outline"
                                @click=${() => {
                                  orderItem.flyaVariableValues?.forEach(variable => variable.revert());
                                  resolve(false);
                                }}
                              >
                                Abbrechen
                              </cr-button>
                              <cr-button
                                @click=${async () => {
                                  if (
                                    await ChromiumDialog.confirm(
                                      'Möchten Sie die ursprünglich angegeben Daten wirklich editieren?',
                                    )
                                  )
                                    resolve(true);
                                }}
                              >
                                Speichern
                              </cr-button>
                            </x-flex>
                          </x-flex>
                        `,
                      })
                    ) {
                      try {
                        if (!this.order) throw new Error('Order is undefined');
                        await ChromiumDialog.waitFor(this.order.save());
                      } catch (err) {
                        logger.error({ err }, 'Es gab ein Problem beim speichern der Bestellung');
                        void ChromiumDialog.alert(
                          'Es gab ein Problem beim Speichern der Bestellung. Bitte versuchen Sie es später erneut!',
                        );
                      }
                    }
                  }}
                >
                  <cr-tooltip>
                    <cr-text> CHANCENvariablen editieren </cr-text>
                  </cr-tooltip>
                </cr-button>
              `
            : nothing}
          <cr-button
            mode="icon"
            icon="visibility"
            @click=${() =>
              goTo(
                (this._product.value as ChancenBrief)!.getGeneratedPDFLink(
                  this.orderItem.value?.flyaVariableValues ?? [],
                ),
                {
                  target: '_blank',
                },
              )}
          >
            <cr-tooltip>
              <cr-text> PDF ansehen </cr-text>
            </cr-tooltip>
          </cr-button>
        `
      : nothing;
  }

  private _quantityInput(): Renderable {
    if (
      !this.orderItem.value ||
      this.orderItem.value instanceof LogistikEntireTrip ||
      this.orderItem.value instanceof LogistikOtherOrder ||
      !this.orderItem.value.quantity
    )
      return nothing;
    return html`
      <cr-input
        type="number"
        .value=${this.orderItem.value.quantity}
        min=${this._product.value?.minOrderAmount ?? 0}
        max=${this._product.value?.maxOrderAmount}
        @value-changed=${(e: ValueEvent<number>) => {
          if (
            !+e.detail.value ||
            !this.orderItem.value ||
            this.orderItem.value instanceof LogistikEntireTrip ||
            this.orderItem.value instanceof LogistikOtherOrder ||
            !this.orderItem.value.quantity
          )
            return;
          this.orderItem.value.quantity = +e.detail.value;
          this.requestUpdate();
        }}
        ${css({ width: '60px' })}
      >
      </cr-input>
    `;
  }

  private _deleteIconButton(): Renderable {
    return html`
      <cr-button
        mode="icon"
        icon="delete"
        @click=${async () => {
          if (!this.orderItem.value || !this.order?.items || !this.index.value) return;
          if (await ChromiumDialog.confirm('Möchten Sie den Artikel wirklich von der Bestellung entfernen?'))
            this.dispatchEvent(new CustomEvent('delete-item'));
        }}
      >
        <cr-tooltip>
          <cr-text> Artikel entfernen </cr-text>
        </cr-tooltip>
      </cr-button>
    `;
  }
}
